import React from 'react'
import { Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getImageUrl, cn } from 'lib/utils'
import { FIGMA_KANBAN_COLUMN } from './figmaKanbanTheme'

function getInterviewScoreClass(score: number, threshold = 70): string {
  if (score >= threshold) {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
  }
  return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
}

interface FigmaKanbanCandidateRowProps {
  name: string
  role?: string
  avatar?: string
  showScore?: boolean
  score?: number
  scorePassed?: boolean
  passesFilters?: boolean
  interviewScore?: number
  interviewScoreThreshold?: number
  onClick?: () => void
}

export function FigmaKanbanCandidateRow({
  name,
  role,
  avatar,
  showScore = false,
  score,
  scorePassed = true,
  passesFilters = true,
  interviewScore,
  interviewScoreThreshold = 70,
  onClick,
}: FigmaKanbanCandidateRowProps) {
  const { t } = useTranslation()
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onClick?.()
      }}
      className="flex w-full items-center border-b border-[#EBEBEB] bg-white text-left transition-colors hover:bg-[#FAFAFA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#19A6A6]/40 dark:border-border dark:bg-card dark:hover:bg-muted/50"
      style={{
        height: FIGMA_KANBAN_COLUMN.candidateRowHeight,
        paddingTop: FIGMA_KANBAN_COLUMN.candidateRowPaddingY,
        paddingBottom: FIGMA_KANBAN_COLUMN.candidateRowPaddingY,
        paddingLeft: FIGMA_KANBAN_COLUMN.candidateRowPaddingX,
        paddingRight: FIGMA_KANBAN_COLUMN.candidateRowPaddingX,
        gap: FIGMA_KANBAN_COLUMN.candidateRowGap,
      }}
    >
      <span className="flex h-6 w-6 shrink-0 overflow-hidden rounded-full bg-[#E8E8E8] dark:bg-muted">
        {avatar ? (
          <img
            src={getImageUrl(avatar) || avatar}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-[#232323] dark:text-foreground">
            {initials}
          </span>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-['Montserrat',sans-serif] text-xs font-medium leading-[1.5] tracking-normal text-[#232323] dark:text-foreground">
          {name}
        </span>
        <span className="flex min-w-0 items-center justify-between gap-2">
          {role ? (
            <span className="block truncate text-[11px] leading-[1.3] text-[#6F6F6F] dark:text-muted-foreground">
              {role}
            </span>
          ) : (
            <span />
          )}
          {(showScore && typeof score === 'number') || interviewScore !== undefined ? (
            <span className="flex shrink-0 items-center gap-1">
              {showScore && typeof score === 'number' ? (
                <span
                  className={cn(
                    'rounded px-2 py-0.5 text-xs font-semibold leading-none',
                    !passesFilters && 'bg-muted text-muted-foreground',
                    passesFilters &&
                      scorePassed &&
                      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
                    passesFilters &&
                      !scorePassed &&
                      'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                  )}
                  aria-label={`Score ${passesFilters ? Math.floor(score) : 0} percent`}
                >
                  {!passesFilters ? '0' : `${Math.floor(score)}%`}
                </span>
              ) : null}
              {interviewScore !== undefined ? (
                <span
                  className={cn(
                    'inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold leading-none',
                    getInterviewScoreClass(interviewScore, interviewScoreThreshold)
                  )}
                  aria-label={t('kanban.figmaInterviewAiScore', 'AI: {{score}}%', {
                    score: Math.floor(interviewScore),
                  })}
                >
                  <Sparkles className="h-3 w-3 shrink-0" aria-hidden />
                  {t('kanban.figmaInterviewAiScore', 'AI: {{score}}%', {
                    score: Math.floor(interviewScore),
                  })}
                </span>
              ) : null}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  )
}
